import React, { useEffect, useState } from 'react';
import { observer, useLocalStore } from 'mobx-react';
import { useHistory, useParams } from 'react-router-dom';
import { runInAction } from 'mobx';
import { AxiosError } from 'axios';

import type { SubsetFrom } from 'Util/TypeHelpers';
import {
	AdminEntity,
	CurriculumSupportEntity,
	IAdminEntityAttributes,
	ICurriculumSupportEntityAttributes,
	IInstitutionAdminEntityAttributes, InstitutionAdminEntity,
	IStudentAdvisorEntityAttributes, StudentAdvisorEntity,
} from 'Models/Entities';
import { useOnMount } from 'Hooks/useMounted';
import useDebounce from 'Hooks/useDebounce';
import alertToast from 'Util/ToastifyUtils';

import SecuredPage from 'Views/Components/Security/SecuredPage';
import Navbar from 'Views/Components/Navbar/Navbar';
import { TextField } from 'Views/Components/TextBox/TextBox';
import { DisplayType } from 'Views/Components/Models/Enums';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { tabs } from 'Views/Pages/Frontend/UserManagement/UserManagementPage';
import { Select } from 'Views/Components/Select/Select';

import { inviteStaffUser } from 'Queries/REST/Register/RegisterRequests';
import {
	userDiscriminatorRoleMap, UserEntities, userModelDisplayNameMap, UserRoles,
} from 'Models/Enums';
import Spinner from 'Views/Components/Spinner/Spinner';
import { getSingleStaff } from 'Queries/GraphQlQueries/User/UserEntityQueries';
import { Conditional } from 'Views/Components/Conditional/Conditional';

type AdministrativeUserTypes = StudentAdvisorEntity | CurriculumSupportEntity | InstitutionAdminEntity | AdminEntity;

type ExistingUserResponseData =
	{ studentAdvisorEntity: IStudentAdvisorEntityAttributes }
	| { curriculumSupportEntity: ICurriculumSupportEntityAttributes }
	| { institutionAdminEntity: IInstitutionAdminEntityAttributes }
	| { adminEntity: IAdminEntityAttributes };

type SelectableDiscriminators = SubsetFrom<
	UserEntities,
	'CurriculumSupportEntity' | 'StudentAdvisorEntity' | 'InstitutionAdminEntity' | 'AdminEntity'
>;

const roleOptions = [
	{
		display: userModelDisplayNameMap.StudentAdvisorEntity,
		value: 'StudentAdvisorEntity',
	},
	{
		display: userModelDisplayNameMap.CurriculumSupportEntity,
		value: 'CurriculumSupportEntity',
	},
	{
		display: userModelDisplayNameMap.InstitutionAdminEntity,
		value: 'InstitutionAdminEntity',
	},
];

interface IStaffFormProps {
	onCancel: () => any;
	afterSave: (user: AdministrativeUserTypes, discriminator: SelectableDiscriminators) => void;
	existingUserId?: string;
	existingUserRole?: UserRoles;
	existingUserDiscriminator?: SelectableDiscriminators
}

export const StaffFormPage = () => {
	const { userId, discriminator } = useParams<{ discriminator?: SelectableDiscriminators, userId?: string }>();
	const history = useHistory();

	return (
		<SecuredPage
			groups={[
				'Super Administrators',
				'Admin',
				'InstitutionAdmin',
			]}
		>
			<div className="body-content">
				<Navbar />
				<StaffForm
					existingUserId={userId}
					existingUserDiscriminator={discriminator}
					afterSave={() => history.push(`/users?tab=${tabs[0]}`)}
					onCancel={history.goBack}
				/>
			</div>
		</SecuredPage>
	);
};

const getUserFromDiscriminator = (discriminator: SelectableDiscriminators, user?: AdministrativeUserTypes) => {
	const copyUserProperties = () => ({
		id: user?.id,
		email: user?.email,
		firstName: user?.firstName,
		lastName: user?.lastName,
	});

	switch (discriminator) {
		case 'AdminEntity':
			return new AdminEntity(copyUserProperties());
		case 'InstitutionAdminEntity':
			return new InstitutionAdminEntity(copyUserProperties());
		case 'CurriculumSupportEntity':
			return new CurriculumSupportEntity(copyUserProperties());
		case 'StudentAdvisorEntity':
		default:
			return new StudentAdvisorEntity(copyUserProperties());
	}
};

export const StaffForm: React.FunctionComponent<IStaffFormProps> = observer(({
	existingUserId,
	existingUserDiscriminator,
	afterSave,
	onCancel,
}) => {
	const formStore = useLocalStore<{
		user: AdministrativeUserTypes;
		discriminator: SelectableDiscriminators;
	}>(() => ({
		user: existingUserDiscriminator
			? getUserFromDiscriminator(existingUserDiscriminator)
			: new InstitutionAdminEntity(),
		discriminator: existingUserDiscriminator || 'InstitutionAdminEntity',
	}));

	useEffect(
		() => {
			runInAction(() => {
				formStore.user = getUserFromDiscriminator(formStore.discriminator, formStore.user);
			});
		},
		[formStore, formStore.discriminator],
	);

	const [loadingExistingUser, setLoadingExistingUser] = useState(!!existingUserId);
	const [isSubmitting, setSubmitting] = useState(false);

	useOnMount(
		async (isMounted, abortSignals) => {
			if (!existingUserId) {
				return;
			}

			const userResponse = await getSingleStaff<ExistingUserResponseData>(
				abortSignals,
				existingUserId,
				formStore.user.getType,
			);

			if (isMounted()) {
				setLoadingExistingUser(false);
				runInAction(() => {
					// because the shape of the returned graphql
					// data is unknown at compile time we must
					// extract the returned data dynamically
					if ('studentAdvisorEntity' in userResponse.data) {
						formStore.user = new StudentAdvisorEntity(userResponse.data.studentAdvisorEntity);
					}

					if ('curriculumSupportEntity' in userResponse.data) {
						formStore.user = new CurriculumSupportEntity(userResponse.data.curriculumSupportEntity);
					}

					if ('institutionAdminEntity' in userResponse.data) {
						formStore.user = new InstitutionAdminEntity(userResponse.data.institutionAdminEntity);
					}

					if ('adminEntity' in userResponse.data) {
						formStore.user = new AdminEntity(userResponse.data.adminEntity);
					}
				});
			}
		},
		[existingUserId],
	);

	const onSave = useDebounce(
		async abortSignals => {
			if (!formStore.user) {
				return;
			}
			const editingExistingUser = !!formStore.user.id;

			// save edit user
			if (editingExistingUser) {
				formStore.user.save()
					.then(() => {
						afterSave(formStore.user, formStore.discriminator);
						alertToast(
							`Staff "${formStore.user.firstName} ${formStore.user.lastName}" saved`,
							'success',
						);
					})
					.catch(requestError => {
						console.error(requestError);
						alertToast(
							'Staff failed to be edited',
							'error',
						);
					})
					.finally(() => {
						setSubmitting(false);
					});
			} else {
				if (isSubmitting || loadingExistingUser) {
					return;
				}
				setSubmitting(true);

				const disc = formStore.discriminator;
				const group = userDiscriminatorRoleMap[disc];

				inviteStaffUser(abortSignals, formStore.user, [group])
					.then(() => {
						alertToast(
							`Staff member "${formStore.user.firstName} ${formStore.user.lastName}" created`,
							'success',
						);
						afterSave(formStore.user, formStore.discriminator);
					})
					.catch((requestError: AxiosError) => {
						const errorCount = requestError.response?.data?.errors.length;
						const formattedErrorMessages = requestError.response?.data?.errors
							// eslint-disable-next-line max-len
							.map((err: { message: string }, index: number) => `${err.message}${index !== errorCount - 1 ? ', ' : ''}`);
						alertToast(
							`Staff member failed to be created - ${formattedErrorMessages}`,
							'error',
						);
					})
					.finally(() => {
						setSubmitting(false);
					});
			}
		},
		[formStore.user?.id],
	);

	const validate = async () => {
		if (!formStore.user) {
			return false;
		}
		formStore.user.clearErrors();
		await formStore.user.validate({
			firstName: {},
			lastName: {},
			email: {},
		});

		return formStore.user.hasValidationError;
	};

	const onSubmit = async (evt: React.FormEvent<HTMLFormElement>) => {
		evt.preventDefault();
		evt.stopPropagation();

		const hasValidationErrors = await validate();
		if (!hasValidationErrors) {
			onSave();
		}
	};

	if (loadingExistingUser) {
		return <Spinner />;
	}

	return (
		<main className="user-form-page student">
			<form onSubmit={onSubmit}>
				<div className="form-fields">
					<h3 className="title">{existingUserId ? 'Edit' : 'Add'} staff</h3>
					<TextField
						model={formStore.user}
						id="firstName"
						modelProperty="firstName"
						name="First name"
						placeholder="Enter first name"
						displayType={DisplayType.BLOCK}
						label="First name"
						isRequired
						errors={formStore.user?.getErrorsForAttribute('firstName')}
					/>
					<TextField
						model={formStore.user}
						id="lastName"
						modelProperty="lastName"
						name="Last name"
						placeholder="Enter last name"
						displayType={DisplayType.BLOCK}
						label="Last name"
						isRequired
						errors={formStore.user?.getErrorsForAttribute('lastName')}
					/>
					<TextField
						model={formStore.user}
						id="email"
						modelProperty="email"
						name="Email"
						placeholder="Enter email"
						displayType={DisplayType.BLOCK}
						label="Email"
						isRequired
						errors={formStore.user?.getErrorsForAttribute('email')}
					/>
					<Conditional
						condition={
							[
								'InstitutionAdminEntity',
								'CurriculumSupportEntity',
								'StudentAdvisorEntity',
							].includes(formStore.discriminator)
						}
						component={() => (
							<Select
								isDisabled={!!formStore.user.id}
								label="Role"
								placeholder="Select role"
								model={formStore}
								modelProperty="discriminator"
								options={roleOptions}
								data-testid="select-role"
								name="role"
							/>
						)}
						componentProps={{}}
					/>
				</div>
				<div className="actions">
					<Button onClick={onCancel}>
						Cancel
					</Button>
					<Button
						type="submit"
						colors={Colors.Primary}
						display={Display.Solid}
						disabled={isSubmitting}
					>
						Save
					</Button>
				</div>
			</form>
		</main>
	);
});
